import { AIR, GRAVITY } from '../constants';
import { traverseVoxels } from '../voxel/raycast';
import type { VoxelWorld } from '../voxel/world';

/**
 * Flight of a thrown or fired projectile against the voxel world: gravity, bounce, roll to a stop and sticking. The server (`stepProjectiles`) and the client's projectile
 * visuals both use these functions, so a grenade the player sees takes the same path as the one that explodes. (The client drew grenades with gravity only, so they fell
 * through the floor while the server's bounced; the server stays the authority for hits on players, pets and the explosion itself.)
 */

/** Longest stretch of a flight, metres, checked against the world in one go. */
export const FLIGHT_MAX_STEP = 0.35;

export interface FlightBody {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
}

export interface FlightHit {
  hit: boolean;
  /** Voxel that was hit. */
  x: number;
  y: number;
  z: number;
  /** Face normal of the hit (0, 0, 0 if the flight started inside the voxel). */
  nx: number;
  ny: number;
  nz: number;
  dist: number;
}

export const newFlightHit = (): FlightHit => ({ hit: false, x: 0, y: 0, z: 0, nx: 0, ny: 0, nz: 0, dist: 0 });

/** What a world-only flight tick ended with. */
export const FlightState = { Flying: 0, Stuck: 1, Ended: 2 } as const;

/** How many equal sub-steps a tick of `dt` seconds is split into so no sub-step is longer than FLIGHT_MAX_STEP. */
export function flightSubsteps(b: FlightBody, dt: number): number {
  const speed = Math.sqrt(b.vx * b.vx + b.vy * b.vy + b.vz * b.vz);
  return Math.max(1, Math.ceil((speed * dt) / FLIGHT_MAX_STEP));
}

/** Applies gravity for `sdt` seconds. Returns the distance this sub-step covers and writes its unit direction into `dir` (length 0 = not moving). */
export function flightAccelerate(b: FlightBody, gravity: number, sdt: number, dir: { x: number; y: number; z: number }): number {
  b.vy -= GRAVITY * gravity * sdt;
  const len = Math.sqrt(b.vx * b.vx + b.vy * b.vy + b.vz * b.vz) * sdt;
  if (len < 1e-6) return 0;
  dir.x = (b.vx * sdt) / len;
  dir.y = (b.vy * sdt) / len;
  dir.z = (b.vz * sdt) / len;
  return len;
}

/** First solid voxel along the sub-step, within `maxDist` metres. */
export function flightWorldHit(world: VoxelWorld, b: FlightBody, dir: { x: number; y: number; z: number }, maxDist: number, out: FlightHit): boolean {
  out.hit = false;
  traverseVoxels(world, b.x, b.y, b.z, dir.x, dir.y, dir.z, maxDist, (x, y, z, dist, nx, ny, nz) => {
    if (world.get(x, y, z) === AIR) return false;
    out.hit = true;
    out.x = x;
    out.y = y;
    out.z = z;
    out.nx = nx;
    out.ny = ny;
    out.nz = nz;
    out.dist = dist;
    return true;
  });
  return out.hit;
}

/** Moves the body along `dir` up to the hit (stopping 2 cm short so it does not sit inside the voxel). */
export function flightMoveToHit(b: FlightBody, dir: { x: number; y: number; z: number }, hit: FlightHit): void {
  const d = Math.max(0, hit.dist - 0.02);
  b.x += dir.x * d;
  b.y += dir.y * d;
  b.z += dir.z * d;
}

/** Bounce off the face that was hit (restitution `bounce`, 20% lost on every contact), and roll to a stop when it comes to rest on a floor. */
export function flightBounce(b: FlightBody, bounce: number, hit: FlightHit): void {
  const nx = hit.nx;
  const ny = hit.ny;
  const nz = hit.nz;
  const vn = b.vx * nx + b.vy * ny + b.vz * nz;
  b.vx = (b.vx - (1 + bounce) * vn * nx) * 0.8;
  b.vy = (b.vy - (1 + bounce) * vn * ny) * 0.8;
  b.vz = (b.vz - (1 + bounce) * vn * nz) * 0.8;
  b.x += nx * 0.03;
  b.y += ny * 0.03;
  b.z += nz * 0.03;
  if (ny > 0 && Math.abs(b.vy) < 1.2) {
    // Resting on the floor: roll to a stop.
    b.vy = 0;
    b.vx *= 0.6;
    b.vz *= 0.6;
  }
}

const dirScratch = { x: 0, y: 0, z: 0 };
const hitScratch = newFlightHit();

/**
 * One tick of flight against the world only (no players, no pets): what the client draws between the server's spawn and end events. Bouncing and sticking
 * projectiles take exactly the server's path; one that hits the world and does not bounce or stick returns `Ended` at the point of impact.
 */
export function flightTick(world: VoxelWorld, b: FlightBody, def: { gravity: number; bounce: number; sticky: boolean }, dt: number): number {
  const steps = flightSubsteps(b, dt);
  const sdt = dt / steps;
  for (let s = 0; s < steps; s++) {
    const len = flightAccelerate(b, def.gravity, sdt, dirScratch);
    if (len === 0) continue;
    if (flightWorldHit(world, b, dirScratch, len, hitScratch)) {
      flightMoveToHit(b, dirScratch, hitScratch);
      if (def.sticky) {
        b.vx = b.vy = b.vz = 0;
        return FlightState.Stuck;
      }
      if (def.bounce > 0 && (hitScratch.nx !== 0 || hitScratch.ny !== 0 || hitScratch.nz !== 0)) {
        flightBounce(b, def.bounce, hitScratch);
        continue;
      }
      return FlightState.Ended;
    }
    b.x += dirScratch.x * len;
    b.y += dirScratch.y * len;
    b.z += dirScratch.z * len;
  }
  return FlightState.Flying;
}
